import React, { useState, useEffect } from 'react';
import { Button, Col, Figure, Form, Modal, Row, Alert } from 'react-bootstrap';
import { useDispatch } from 'react-redux';
import defaultLogo from '../../assets/images/avatar.png';
import { useTranslation } from 'react-i18next';
import { saveCustomersInfo } from '../../pages/Customers/CustomersSlice';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { resizeFile } from '../../common/uploadImage';
import { toast } from 'react-toastify';

const phoneRegExp =
  /^((\\+[1-9]{1,4}[ \\-]*)|(\\([0-9]{2,3}\\)[ \\-]*)|([0-9]{2,4})[ \\-]*)*?[0-9]{3,4}?[ \\-]*[0-9]{3,4}?$/;

const addCustomerSchema = yup.object().shape({
  name: yup.string().required('Name is a required field.'),
  phone: yup
    .string()
    .required('Phone number is a required field.')
    .min(10, 'Phone must be at least 10 numbers.')
    .matches(phoneRegExp, 'Phone number is not valid.'),
  email: yup
    .string()
    .required('Email is a required field.')
    .email('Invalid email format.'),
  address: yup.string(),
  file: yup.string().required('Logo is a required field.'),
});

const ModalAddCustomer = ({ show, setShow }) => {
  const token = localStorage.getItem('token');
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const [validated, setValidated] = useState(false);
  const [isChangeLogo, setIsChangeLogo] = useState();

  const handleClose = () => {
    resetField('name');
    resetField('phone');
    resetField('email');
    resetField('address');
    resetField('file');
    setIsChangeLogo();
    setValidated(false);
    setShow(false);
  };

  const {
    register,
    resetField,
    getValues,
    setValue,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(addCustomerSchema),
  });

  const onChangeFile = async (file) => {
    try {
      const image = await resizeFile(file);
      setValue('file', image);
      setIsChangeLogo(image);
    } catch (err) {
      toast.error(err);
    }
  };

  const onSubmit = () => {
    const name = getValues('name');
    const phone = getValues('phone');
    const email = getValues('email');
    const address = getValues('address');
    const file = getValues('file');
    dispatch(
      saveCustomersInfo({
        token,
        name,
        phone,
        email,
        address,
        file,
      })
    );
    resetField('name');
    resetField('phone');
    resetField('email');
    resetField('address');
    resetField('file');
    setIsChangeLogo();
    setValidated(false);
    setShow(false);
  };

  useEffect(() => {
    if (errors.name || errors.phone || errors.email || errors.file) {
      setValidated(true);
    } else setValidated(false);
  }, [errors]);

  return (
    <Modal onHide={handleClose} size="lg" show={show}>
      <Modal.Header closeButton>
        <Modal.Title>{t('add-new-customer')}</Modal.Title>
      </Modal.Header>
      <Form noValidate validated={validated} onSubmit={handleSubmit(onSubmit)}>
        <Modal.Body>
          <Row>
            <Col sm="4">
              <Figure.Image
                height={300}
                width="100%"
                style={{ borderRadius: '10px', objectFit: 'scale-down' }}
                src={isChangeLogo ? isChangeLogo : defaultLogo}
                alt="logo"
              />
              <Form.Group className="position-relative mb-3">
                <Form.Label>
                  <span>
                    <i
                      class="fa fa-asterisk"
                      style={{ color: 'red', fontSize: '10px' }}
                      aria-hidden="true"
                    ></i>{' '}
                  </span>
                  {t('add-logo')}
                </Form.Label>
                <Form.Control
                  required
                  type="file"
                  accept="image/*"
                  onChange={(e) => onChangeFile(e.target.files[0])}
                />
                {errors.file && (
                  <Alert
                    variant="danger"
                    style={{
                      padding: '5px',
                      height: '32px',
                      marginTop: '4px',
                    }}
                  >
                    <p>{errors.file.message}</p>
                  </Alert>
                )}
              </Form.Group>
            </Col>
            <Col sm="8">
              <Form.Group as={Col} controlId="customerName">
                <Form.Label>
                  <span>
                    <i
                      class="fa fa-asterisk"
                      style={{ color: 'red', fontSize: '10px' }}
                      aria-hidden="true"
                    ></i>{' '}
                  </span>
                  {t('customer-name')}:
                </Form.Label>
                <Form.Control
                  required
                  type="text"
                  placeholder={t('customer-name')}
                  {...register('name')}
                />
                {errors.name && (
                  <Alert
                    variant="danger"
                    style={{
                      padding: '5px',
                      height: '32px',
                      marginTop: '4px',
                    }}
                  >
                    <p>{errors.name.message}</p>
                  </Alert>
                )}
              </Form.Group>
              <Form.Group as={Col} controlId="phoneNumber">
                <Form.Label>
                  <span>
                    <i
                      class="fa fa-asterisk"
                      style={{ color: 'red', fontSize: '10px' }}
                      aria-hidden="true"
                    ></i>{' '}
                  </span>
                  {t('phone')}:
                </Form.Label>
                <Form.Control
                  required
                  type="text"
                  placeholder={t('phone')}
                  {...register('phone')}
                />
                {errors.phone && (
                  <Alert
                    variant="danger"
                    style={{
                      padding: '5px',
                      height: '32px',
                      marginTop: '4px',
                    }}
                  >
                    <p>{errors.phone.message}</p>
                  </Alert>
                )}
              </Form.Group>
              <Form.Group as={Col} controlId="email">
                <Form.Label>
                  <span>
                    <i
                      class="fa fa-asterisk"
                      style={{ color: 'red', fontSize: '10px' }}
                      aria-hidden="true"
                    ></i>{' '}
                  </span>
                  {t('email')}:
                </Form.Label>
                <Form.Control
                  required
                  type="text"
                  placeholder={t('email')}
                  {...register('email')}
                />
                {errors.email && (
                  <Alert
                    variant="danger"
                    style={{
                      padding: '5px',
                      height: '32px',
                      marginTop: '4px',
                    }}
                  >
                    <p>{errors.email.message}</p>
                  </Alert>
                )}
              </Form.Group>
              <Form.Group as={Col} controlId="address">
                <Form.Label>{t('address')}:</Form.Label>
                <Form.Control
                  placeholder={t('address')}
                  as="textarea"
                  rows={3}
                  {...register('address')}
                />
                {errors.address && (
                  <Alert
                    variant="danger"
                    style={{
                      padding: '5px',
                      height: '32px',
                      marginTop: '4px',
                    }}
                  >
                    <p>{errors.address.message}</p>
                  </Alert>
                )}
              </Form.Group>
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose}>
            {t('close')}
          </Button>
          <Button variant="primary" type="submit">
            {t('add-new')}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default ModalAddCustomer;
